import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUnitById, createUnit, updateUnit } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function AddEditUnit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("units");
  const { t: tCommon } = useTranslation("common");

  const [form, setForm] = useState({ name: "", symbol: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      getUnitById(token, id).then((u) => {
        setForm({ name: u.name || "", symbol: u.symbol || "" });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load unit")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Unit name is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = { name: form.name.trim(), symbol: form.symbol.trim() };

    try {
      if (id) {
        await updateUnit(token, id, payload);
        notify.success(t("notify_updated", "Unit updated successfully"));
      } else {
        await createUnit(token, payload);
        notify.success(t("notify_created", "Unit created successfully"));
      }
      navigate("/units");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/units")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Unit") : t("add_title", "Add New Unit")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "e.g. \"Piece\", \"Kilogram\", \"Liter\"")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <FloatingInput label={t("field_name_label", "Unit Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Units} />
          <FloatingInput label={t("field_symbol_label", "Symbol (optional)")} value={form.symbol} onChange={setField("symbol")} />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_unit", "Update Unit") : t("btn_create_unit", "Create Unit")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/units")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
